import React, { useState } from 'react';
import { copilotConfig } from '@components/Copilot/constants';
import { getAiMagicIcon } from '@components/Copilot/utils';
import { AvButton, TextInput } from '@fabric-ui/components';
import { Send } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize, noop } from '@fabric-ui/utils';
import { IconButton } from '@mui/material';
import { useAvCopilotContext } from '../../context/AvCopilotProvider';

interface IPromptInput {
  onSend: (prompt: string) => void;
  disabled?: boolean;
}

const PromptInput: React.FC<IPromptInput> = ({ onSend = noop, disabled = false }) => {
  const [promptValue, setPromptValue] = useState<string>('');
  const { lastSessionProps, setIsDefaultPromptsOpen, messages } = useAvCopilotContext();

  const isDisabled = disabled || !promptValue.trim();
  const placeholderText = copilotConfig[lastSessionProps.agentId]?.placeholderText || 'Ask about your data...';

  const handleSend = () => {
    if (isDisabled) {
      return;
    }
    onSend(promptValue);
    setPromptValue('');
  };

  return (
    <TextInput
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handleSend();
        }
      }}
      multiline
      maxRows={4}
      sx={{
        alignSelf: 'center',
        maxWidth: '100%',
        backgroundColor: theme => theme.palette.white.main,
        borderRadius: '80px',
        '&.MuiFormControl-root': {
          '.MuiInputBase-root': {
            alignItems: 'center',
            py: '10px',
            pl: '10px',
          },
        },
        '.MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-multiline.MuiInputBase-sizeSmall': {
          minHeight: 48,
          pl: '10px',
        },
        '.MuiInputBase-root fieldset.MuiOutlinedInput-notchedOutline': {
          overflow: 'hidden',
          borderRadius: '80px',
          border: 'none',
          boxShadow: '0px 8px 24px -4px rgba(0, 0, 0, 0.15)',
        },
      }}
      icon={
        <AvButton
          aria-label="default-prompts"
          tooltip="Default Prompts"
          toolTipProps={{ popperProps: { placement: 'top' } }}
          disabled={disabled || messages.length === 0}
          sx={{ pointerEvents: 'all' }}
          onClick={() => setIsDefaultPromptsOpen(prev => !prev)}>
          {getAiMagicIcon(28, iconSize(20))}
        </AvButton>
      }
      actionIcons={[
        <IconButton
          aria-label="send-icon"
          disabled={isDisabled}
          color={promptValue ? 'primary' : 'default'}
          sx={{
            ...flex.center,
            cursor: promptValue ? 'pointer' : 'default',
            '&.MuiIconButton-root:before': { borderRadius: '50%' },
          }}
          onClick={handleSend}>
          <Send style={{ ...iconSize(20) }} />
        </IconButton>,
      ]}
      placeholder={placeholderText}
      disableClearButton
      value={promptValue}
      onChange={setPromptValue}
      size="small"
    />
  );
};

export default PromptInput;
